import React, { useEffect, useRef, useState, CSSProperties } from "react";
import UpDownArrow from "../components/UpDownArrow";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useAppContext } from "../components/AppContext";
import {
  darkModeBorder,
  darkModeText,
  darkModeWidgetBackground,
  lightModeBorder,
  lightModeText,
  lightModeWidgetBackground,
  negativeRed,
  positiveGreen,
  primaryBlueLight,
} from "../util/style";

interface MarketProps {
  handleWatchListAddRemove: (ticker: string) => void;
}

const Market: React.FC<MarketProps> = ({ handleWatchListAddRemove }) => {
  const {
    darkMode,
    user,
    isLogged,
    defaultWatchList,
    selectedTicker,
    setSelectedTicker,
    getFont,
    lastPrice,
    prevClosePrice,
    longName,
    numFormat,
    checkHealthIfServiceDown,
    expandWatchlist,
    setExpandWatchlist,
    setExpandChart,
  } = useAppContext() || {};

  const prevDataRef = useRef<Record<string, { price?: number; prevClose?: number }>>({});
  const [highlighted, setHighlighted] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const changed: Record<string, boolean> = {};
    const targetList = isLogged ? user?.watchlist : defaultWatchList;
    targetList?.forEach((ticker: string) => {
      const prev = prevDataRef.current[ticker] || {};
      const curr = {
        price: lastPrice?.[ticker],
        prevClose: prevClosePrice?.[ticker],
      };

      if (prev.price !== curr.price || prev.prevClose !== curr.prevClose) {
        changed[ticker] = true;
        setTimeout(() => {
          setHighlighted((prev) => ({ ...prev, [ticker]: false }));
        }, 1000);
      }

      prevDataRef.current[ticker] = curr;
    });

    if (Object.keys(changed).length > 0) {
      setHighlighted((prev) => ({ ...prev, ...changed }));
    }
  }, [lastPrice, prevClosePrice, user, isLogged, defaultWatchList]);

  const tableStyle: {
    row: CSSProperties;
    cellLeft: CSSProperties;
    cellRight: CSSProperties;
  } = {
    row: {
      borderBottom: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "1px 10px",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "1px",
    },
    cellLeft: {
      textAlign: "left" as const,
    },
    cellRight: {
      textAlign: "right" as const,
    },
  };

  return (
    <div style={{}}>
      <table
        style={{
          borderCollapse: "collapse",
          tableLayout: "auto",
          width: "100%",
          fontSize: getFont?.(12),
          background: darkMode
            ? darkModeWidgetBackground
            : lightModeWidgetBackground,
          height: "fit-content",
        }}
      >
        <thead
          style={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            background: darkMode
              ? darkModeWidgetBackground
              : lightModeWidgetBackground,
          }}
        >
          <tr>
            <th
              style={{
                ...tableStyle.row,
                ...tableStyle.cellLeft,
                paddingTop: "10px",
                fontWeight: "500",
              }}
            >
              Ticker
            </th>
            <th
              style={{
                ...tableStyle.row,
                ...tableStyle.cellRight,
                paddingTop: "10px",
                fontWeight: "500",
                width: "15%",
              }}
            >
              %&nbsp;Chg
            </th>
            <th
              style={{
                ...tableStyle.row,
                ...tableStyle.cellRight,
                paddingTop: "10px",
                fontWeight: "500",
                width: "15%",
              }}
            >
              $&nbsp;Chg
            </th>
            <th
              style={{
                ...tableStyle.row,
                paddingTop: "10px",
                fontWeight: "500",
                ...tableStyle.cellRight,
                width: "15%",
              }}
            >
              Price
            </th>
            <th
              style={{
                ...tableStyle.row,
                paddingTop: "10px",
                fontWeight: "500",
                ...tableStyle.cellRight,
                width: "18%",
              }}
            >
              PrevClose
            </th>
            {isLogged && (
              <th
                style={{
                  ...tableStyle.row,
                  ...tableStyle.cellRight,
                  paddingTop: "10px",
                  fontWeight: "500",
                  maxWidth: "20px",
                  width: "5%",
                }}
              ></th>
            )}
          </tr>
        </thead>
        <tbody>
          {((isLogged && user?.watchlist) || defaultWatchList)
            ?.sort((a: string, b: string) => a.localeCompare(b))
            ?.map((eachTicker: string, idx: number) => (
              <tr
                key={idx}
                onClick={() => {
                  setSelectedTicker?.(eachTicker.toUpperCase());
                }}
                className={`hoverWatchlistBackground`}
                style={{
                  cursor: "pointer",
                  width: "100%",
                  background:
                    eachTicker === selectedTicker ? primaryBlueLight : "",
                  transition: "all 0.1s ease",
                }}
              >
                <td
                  style={{
                    ...tableStyle.row,
                    ...tableStyle.cellLeft,
                  }}
                >
                  <div style={{ fontSize: getFont?.(14) }}>{eachTicker}</div>
                  <div
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      fontSize: getFont?.(9),
                    }}
                  >
                    {longName?.[eachTicker]}&nbsp;
                  </div>
                </td>
                <td
                  style={{
                    ...tableStyle.row,
                    ...tableStyle.cellRight,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      marginLeft: "auto",
                      width: "fit-content",
                    }}
                    className={`${highlighted[eachTicker] ? "flash" : ""}`}
                  >
                    <UpDownArrow
                      number={
                        ((lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0)) /
                        (prevClosePrice?.[eachTicker] || 1)
                      }
                    />
                    <div
                      style={{
                        fontWeight: "600",
                        color:
                          isNaN(
                            (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0),
                          ) ||
                          (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0) === 0
                            ? darkMode
                              ? darkModeText
                              : lightModeText
                            : (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0) < 0
                              ? negativeRed
                              : positiveGreen,
                      }}
                    >
                      {numFormat?.(
                        Math.abs(
                          (100 *
                            ((lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0))) /
                            (prevClosePrice?.[eachTicker] || 1),
                        ),
                      )}
                      %
                    </div>
                  </div>
                </td>
                <td style={{ ...tableStyle.row, ...tableStyle.cellRight }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      marginLeft: "auto",
                      width: "fit-content",
                    }}
                    className={`${highlighted[eachTicker] ? "flash" : ""}`}
                  >
                    <UpDownArrow
                      number={(lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0)}
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          color:
                            isNaN(
                              (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0),
                            ) ||
                            (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0) === 0
                              ? darkMode
                                ? darkModeText
                                : lightModeText
                              : (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0) < 0
                                ? negativeRed
                                : positiveGreen,
                        }}
                      >
                        $
                        {numFormat?.(
                          Math.abs(
                            (lastPrice?.[eachTicker] || 0) - (prevClosePrice?.[eachTicker] || 0),
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  style={{
                    ...tableStyle.row,
                    ...tableStyle.cellRight,
                  }}
                >
                  <div
                    style={{ marginLeft: "auto", width: "fit-content" }}
                    className={`${highlighted[eachTicker] ? "flash" : ""}`}
                  >
                    ${numFormat?.(lastPrice?.[eachTicker] || 0)}
                  </div>
                </td>
                <td style={{ ...tableStyle.row, ...tableStyle.cellRight }}>
                  ${numFormat?.(prevClosePrice?.[eachTicker] || 0)}
                </td>
                {isLogged && (
                  <td
                    style={{
                      ...tableStyle.row,
                      ...tableStyle.cellRight,
                      textAlign: "center",
                      verticalAlign: "middle",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWatchListAddRemove(eachTicker.toUpperCase());
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <HighlightOffIcon
                        style={{
                          color: negativeRed,
                          fontSize: getFont?.(12),
                          transform: "translateY(0px)",
                        }}
                      />
                    </div>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default Market;
